import {
    CircularProgress,
    ListItemIcon,
    ListItemText,
    MenuItem,
    type MenuItemProps,
} from '@mui/material';
import type { ReactNode } from 'react';
import { ICON_PX } from '~/theme/iconSizes';
import { LABEL_LINE_PX } from './menuRowSizes';

/**
 * An action row of a menu: its icon in the theme's menu icon colour, or red
 * when destructive, then its label, with a note under the label when given.
 * A disabled reason disables the row and is its note; a busy row shows its
 * progress in place of its icon. A disabled, refused or busy row takes no
 * click. The icon is a drawn glyph at the theme's menu icon size.
 *
 * The other MenuItem props pass through: a Menu steers focus through its
 * direct children's props, so a row placed directly in a Menu stays
 * reachable from the keyboard.
 */
export default function MenuAction({
    icon,
    label,
    onClick,
    destructive = false,
    disabled = false,
    busy = false,
    disabledReason,
    note,
    trailing,
    ...menuItemProps
}: Omit<MenuItemProps, 'onClick' | 'disabled' | 'children' | 'sx'> & {
    icon: ReactNode;
    label: ReactNode;
    onClick: () => void;
    destructive?: boolean;
    disabled?: boolean;
    busy?: boolean;
    /** Why the action cannot run now; it is then disabled and says so under its label. */
    disabledReason?: string;
    /** What the action means, under its label. */
    note?: ReactNode;
    /** A mark at the row's end, such as the chevron of a submenu. */
    trailing?: ReactNode;
}) {
    const secondary = disabledReason ?? note;
    const isDisabled = disabled || busy || disabledReason !== undefined;
    return (
        <MenuItem
            {...menuItemProps}
            disabled={isDisabled}
            // The disabled styling alone stops only the pointer, not a click
            // sent another way.
            onClick={isDisabled ? undefined : onClick}
            sx={{
                // With a note under the label, the icon stays on the label's
                // line.
                ...(secondary ? { alignItems: 'flex-start' } : {}),
                // The theme colours a menu row's icon from the row; a
                // destructive row's icon is red, set at the same level.
                ...(destructive
                    ? { '& .MuiListItemIcon-root': { color: 'error.main' } }
                    : {}),
            }}
        >
            <ListItemIcon
                sx={{
                    width: ICON_PX,
                    height: LABEL_LINE_PX,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {busy ? <CircularProgress size={ICON_PX - 2} /> : icon}
            </ListItemIcon>
            <ListItemText
                sx={destructive ? { color: 'error.main' } : undefined}
                secondary={secondary}
                secondaryTypographyProps={{ whiteSpace: 'normal' }}
            >
                {label}
            </ListItemText>
            {trailing}
        </MenuItem>
    );
}
